import { useState, useMemo } from "react";
import SectionCard from "@/components/SectionCard";
import BarChart from "@/components/charts/BarChart";
import { comparativaData, DATOS_POR_PERIODO } from "@/mocks/data";
import { comparativaPorCentro, comparativaPorEspecialidad } from "@/mocks/comparativaDatasets";
import { useFilters } from "@/context/FiltersContext";
import { formatNum, formatPct } from "@/lib/constants";
import { ArrowUpRight, ArrowDownRight, ArrowLeftRight, CalendarDays, Hospital, Stethoscope, Users, Clock, Smile, TrendingDown } from "lucide-react";

const MODOS = [
  { key: "periodo", label: "Período", icon: CalendarDays },
  { key: "centro", label: "Centro", icon: Hospital },
  { key: "especialidad", label: "Especialidad", icon: Stethoscope },
];

// Períodos disponibles con datos reales
const PERIODOS_DISPONIBLES = ["Abril 2026", "Marzo 2026", "Q1 2026", "Q4 2025"];

/**
 * Construye funnel comparado desde datos reales de DATOS_POR_PERIODO.
 */
function buildFunnelComparado(periodoA, periodoB) {
  const a = DATOS_POR_PERIODO[periodoA];
  const b = DATOS_POR_PERIODO[periodoB];
  if (!a || !b) return [];

  const rows = [
    { etapa: "Citas PRM", va: a.prm.total_citas, vb: b.prm.total_citas },
    { etapa: "Consultas DG", va: a.dg.episodios, vb: b.dg.episodios },
    { etapa: "Lab Modulab", va: a.modulab.ordenes, vb: b.modulab.ordenes },
  ];

  return rows.map((r) => ({
    etapa: r.etapa,
    a: r.va,
    b: r.vb,
    delta_pct: r.va === 0 ? 0 : Number(((r.vb - r.va) / r.va * 100).toFixed(1)),
  }));
}

export default function Comparativa() {
  const [modo, setModo] = useState("periodo");
  const { filters } = useFilters();

  const config = useMemo(() => {
    if (modo === "periodo") {
      return {
        opcionesA: PERIODOS_DISPONIBLES,
        opcionesB: PERIODOS_DISPONIBLES,
        defaultA: "Marzo 2026",
        defaultB: "Abril 2026",
        labelA: "Período A",
        labelB: "Período B",
      };
    }
    if (modo === "centro") {
      return {
        opcionesA: comparativaPorCentro.opciones,
        opcionesB: comparativaPorCentro.opciones,
        defaultA: "CMSJ San Joaquín",
        defaultB: "Hospital Clínico UC",
        labelA: "Centro A",
        labelB: "Centro B",
        dataset: comparativaPorCentro,
      };
    }
    return {
      opcionesA: comparativaPorEspecialidad.opciones,
      opcionesB: comparativaPorEspecialidad.opciones,
      defaultA: "Medicina Familiar",
      defaultB: "Cardiología",
      labelA: "Especialidad A",
      labelB: "Especialidad B",
      dataset: comparativaPorEspecialidad,
    };
  }, [modo]);

  const [a, setA] = useState(config.defaultA);
  const [b, setB] = useState(config.defaultB);

  // Resincronizar valores al cambiar modo
  const handleModoChange = (m) => {
    setModo(m);
    if (m === "periodo") {
      setA("Marzo 2026");
      setB("Abril 2026");
    } else if (m === "centro") {
      setA("CMSJ San Joaquín");
      setB("Hospital Clínico UC");
    } else {
      setA("Medicina Familiar");
      setB("Cardiología");
    }
  };

  // Construir datos comparados dinámicamente
  const { funnelComparado, fugaA, fugaB, tiempoA, tiempoB, npsA, npsB, volA, volB } = useMemo(() => {
    if (modo === "periodo") {
      // Usar datos REALES de DATOS_POR_PERIODO
      const funnel = buildFunnelComparado(a, b);
      const dataA = DATOS_POR_PERIODO[a];
      const dataB = DATOS_POR_PERIODO[b];
      return {
        funnelComparado: funnel,
        fugaA: 0, fugaB: 0,
        tiempoA: 0, tiempoB: 0,
        npsA: 0, npsB: 0,
        volA: dataA ? dataA.dg.pacientes : 0,
        volB: dataB ? dataB.dg.pacientes : 0,
      };
    }
    const ds = config.dataset.datos;
    const da = ds[a];
    const db = ds[b];
    if (!da || !db) {
      return { funnelComparado: [], fugaA: 0, fugaB: 0, tiempoA: 0, tiempoB: 0, npsA: 0, npsB: 0, volA: 0, volB: 0 };
    }
    const etapas = da.funnel.map((e) => e.etapa);
    const funnelComparado = etapas.map((etapa, i) => {
      const va = da.funnel[i]?.valor ?? 0;
      const vb = db.funnel[i]?.valor ?? 0;
      const delta_pct = va === 0 ? 0 : ((vb - va) / va) * 100;
      return { etapa, a: va, b: vb, delta_pct: Number(delta_pct.toFixed(1)) };
    });
    return {
      funnelComparado,
      fugaA: da.fuga_global, fugaB: db.fuga_global,
      tiempoA: da.tiempo_espera_min, tiempoB: db.tiempo_espera_min,
      npsA: da.nps, npsB: db.nps,
      volA: da.volumen_total, volB: db.volumen_total,
    };
  }, [modo, a, b, config]);

  // Ganador de cada métrica
  const MetricaCard = ({ icon: Icon, label, valA, valB, suffix = "", menorEsMejor = false, testid }) => {
    const deltaAbs = valB - valA;
    const deltaPct = valA ? (deltaAbs / valA) * 100 : 0;
    const ganadorB = menorEsMejor ? deltaAbs < 0 : deltaAbs > 0;
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
        <div className="flex items-center gap-2 text-slate-500">
          <Icon className="w-3.5 h-3.5" />
          <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] text-slate-400 uppercase">{config.labelA}</div>
            <div className="text-lg font-bold text-slate-700 tabular-nums">{typeof valA === "number" ? formatNum(valA) : valA}{suffix}</div>
          </div>
          <div>
            <div className="text-[10px] text-violet-400 uppercase">{config.labelB}</div>
            <div className="text-lg font-bold text-violet-700 tabular-nums">{typeof valB === "number" ? formatNum(valB) : valB}{suffix}</div>
          </div>
        </div>
        <div className={`mt-3 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
          ganadorB ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
        }`}>
          {deltaAbs >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {deltaAbs >= 0 ? "+" : ""}{deltaPct.toFixed(1)}%
          <span className="opacity-60">· {ganadorB ? "mejor B" : "mejor A"}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="comparativa-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Comparativa</h1>
        <p className="text-sm text-slate-500 mt-1">Compara entre períodos, centros o especialidades · {filters.periodo}</p>
      </div>

      {/* Selector de modo */}
      <div className="flex flex-wrap gap-2" data-testid="modo-selector">
        {MODOS.map((m) => (
          <button
            key={m.key}
            data-testid={`modo-${m.key}`}
            onClick={() => handleModoChange(m.key)}
            className={`inline-flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-semibold transition border ${
              modo === m.key
                ? "bg-gradient-to-r from-violet-600 to-blue-500 text-white border-transparent shadow-md shadow-violet-300/40"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:-translate-y-0.5"
            }`}
          >
            <m.icon className="w-4 h-4" />
            {m.label}
            {m.key === "periodo" && <span className="text-[10px] opacity-70">REAL</span>}
            {m.key !== "periodo" && <span className="text-[10px] opacity-70">⚠️</span>}
          </button>
        ))}
      </div>

      {/* Nota de datos según modo */}
      {modo === "periodo" && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
          <span>✓</span> Datos reales — PRM, DG, Modulab, IMED por período
        </div>
      )}
      {modo !== "periodo" && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">
          <span>⚠️</span> Datos estimados — comparativa por {modo === "centro" ? "centro" : "especialidad"} basada en distribuciones proporcionales
        </div>
      )}

      {/* Selectores A / B */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-center gap-4" data-testid="compare-selectors">
        <div className="flex-1 min-w-[220px]">
          <label htmlFor="select-a" className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{config.labelA}</label>
          <select id="select-a" value={a} onChange={(e) => setA(e.target.value)}
                  className="w-full mt-1 h-10 rounded-xl border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none"
                  data-testid="select-a">
            {config.opcionesA.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
        <ArrowLeftRight className="w-6 h-6 text-violet-500 mt-4" />
        <div className="flex-1 min-w-[220px]">
          <label htmlFor="select-b" className="text-[10px] uppercase tracking-wider font-semibold text-violet-600">{config.labelB}</label>
          <select id="select-b" value={b} onChange={(e) => setB(e.target.value)}
                  className="w-full mt-1 h-10 rounded-xl border border-violet-200 px-3 text-sm focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none"
                  data-testid="select-b">
            {config.opcionesB.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
      </div>

      {/* Métricas clave comparadas — solo para centro / especialidad */}
      {modo !== "periodo" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricaCard icon={Users} label="Volumen total" valA={volA} valB={volB} testid="metrica-volumen" />
          <MetricaCard icon={TrendingDown} label="Fuga global" valA={fugaA} valB={fugaB} suffix="%" menorEsMejor testid="metrica-fuga" />
          <MetricaCard icon={Clock} label="Tiempo espera (min)" valA={tiempoA} valB={tiempoB} menorEsMejor testid="metrica-tiempo" />
          <MetricaCard icon={Smile} label="NPS" valA={npsA} valB={npsB} testid="metrica-nps" />
        </div>
      )}

      {/* Métricas clave comparadas — período con datos reales */}
      {modo === "periodo" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricaCard icon={Users} label="Pacientes DG" valA={volA} valB={volB} testid="metrica-volumen" />
          <MetricaCard
            icon={TrendingDown}
            label="Citas PRM"
            valA={DATOS_POR_PERIODO[a]?.prm.total_citas ?? 0}
            valB={DATOS_POR_PERIODO[b]?.prm.total_citas ?? 0}
            testid="metrica-citas"
          />
          <MetricaCard
            icon={Clock}
            label="Órdenes Modulab"
            valA={DATOS_POR_PERIODO[a]?.modulab.ordenes ?? 0}
            valB={DATOS_POR_PERIODO[b]?.modulab.ordenes ?? 0}
            testid="metrica-modulab"
          />
          <MetricaCard
            icon={Smile}
            label="Bonos IMED"
            valA={DATOS_POR_PERIODO[a]?.imed.bonos ?? 0}
            valB={DATOS_POR_PERIODO[b]?.imed.bonos ?? 0}
            testid="metrica-imed"
          />
        </div>
      )}

      <SectionCard title="Funnel comparado" subtitle={`${a} vs ${b}${modo === "periodo" ? " · Datos reales" : " · ⚠️ Estimado"}`} testid="funnel-comparado">
        <BarChart
          categories={funnelComparado.map((f) => f.etapa)}
          series={[
            { name: a, data: funnelComparado.map((f) => f.a) },
            { name: b, data: funnelComparado.map((f) => f.b) },
          ]}
          colors={["#CBD5E1", "#7C3AED"]}
          height={360}
        />
      </SectionCard>

      <SectionCard title="Delta por etapa" subtitle={modo === "periodo" ? "Datos reales" : "⚠️ Estimado"} testid="delta-etapas">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <th className="py-3 px-4">Etapa</th>
                <th className="py-3 px-4 text-right">{a}</th>
                <th className="py-3 px-4 text-right">{b}</th>
                <th className="py-3 px-4 text-right">Delta</th>
                <th className="py-3 px-4">Tendencia</th>
              </tr>
            </thead>
            <tbody>
              {funnelComparado.map((r, i) => {
                const positive = r.delta_pct >= 0;
                return (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition" data-testid={`delta-row-${i}`}>
                    <td className="py-3 px-4 font-semibold text-slate-800">{r.etapa}</td>
                    <td className="py-3 px-4 text-right tabular-nums text-slate-600">{formatNum(r.a)}</td>
                    <td className="py-3 px-4 text-right tabular-nums text-slate-900 font-semibold">{formatNum(r.b)}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                        positive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"
                      }`}>
                        {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {formatPct(r.delta_pct)}
                      </span>
                    </td>
                    <td className="py-3 px-4 w-56">
                      <div className="h-2 bg-slate-100 rounded-full relative overflow-hidden">
                        <div
                          className={`absolute top-0 h-full ${positive ? "bg-emerald-500" : "bg-rose-500"}`}
                          style={{ width: `${Math.min(Math.abs(r.delta_pct) * 4, 50)}%`, left: positive ? "50%" : `${50 - Math.min(Math.abs(r.delta_pct) * 4, 50)}%` }}
                        />
                        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-300" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Sección específica de período: fuga por tipo de viaje (mock) */}
      {modo === "periodo" && (
        <SectionCard title="Tasa de fuga por tipo de viaje" subtitle="Delta en puntos porcentuales · ⚠️ Datos estimados" testid="fuga-comparada">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {comparativaData.tasa_fuga_comparada.map((t) => {
              const negative = t.delta > 0;
              return (
                <div key={t.ambito} className="rounded-xl border border-slate-200 p-5 bg-white hover:shadow-lg hover:shadow-violet-200/40 hover:-translate-y-0.5 transition" data-testid={`fuga-${t.ambito}`}>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t.ambito}</div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-2xl font-bold text-slate-900 tabular-nums">{formatPct(t.b)}</span>
                    <span className="text-xs text-slate-500 tabular-nums">{a}: {formatPct(t.a)}</span>
                  </div>
                  <div className={`mt-2 inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                    negative ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"
                  }`}>
                    {negative ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                    {t.delta > 0 ? "+" : ""}{t.delta}pp
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
